/**
 * The input rules a typed value meets before it is stored (piece 2, the
 * validators; decision 0290): a person's name in title case (a name typed wholly in capitals lowered, decision 7700), an email
 * lower-cased, a phone number E.164 where it places itself, a US postal code
 * in its five- or nine-digit shape. One routine per rule, run at the form's
 * blur and again at the write, so a value stored by any path reads one way.
 * 🔨 A rule reformats and never refuses: the validators decide what is
 * refused, these decide how an accepted value is written.
 */
import { validatePostalCode } from '../validators';
import { PhoneNumberError, toE164 } from './phone';

/** The suffixes a name carries in capitals on purpose; every other word typed wholly in capitals is lowered after its first letter (decision 7700). */
const NAME_SUFFIXES: ReadonlySet<string> = new Set(['II', 'III', 'IV']);

/** A word of two letters or more typed wholly in capitals, a suffix aside, with every capital that follows a letter lowered; the capital after a hyphen, an apostrophe or a period stays. */
function lowerShoutedWord(word: string): string {
  const letters = word.replace(/[^\p{L}]/gu, '');
  if ([...letters].length < 2 || letters !== letters.toLocaleUpperCase() || NAME_SUFFIXES.has(letters)) return word;
  return word.replace(/(?<=\p{L})\p{Lu}/gu, (letter) => letter.toLocaleLowerCase());
}

/**
 * The first letter of every word, and the letter after a hyphen or an apostrophe, capitalized. A word of two
 * letters or more typed wholly in capitals is lowered after those letters, so BRITTANY reads Brittany, O'BRIEN
 * O'Brien and SMITH-JONES Smith-Jones; a single letter and the suffixes II, III and IV keep their capitals, and a
 * word with mixed case is left as typed, so McDonald stays McDonald (decisions 0290, 7700). MCDONALD reads
 * Mcdonald, the priced cost, corrected by hand.
 */
export function titleCaseName(value: string): string {
  const collapsed = value.replace(/\s+/gu, ' ').trim().split(' ').map(lowerShoutedWord).join(' ');
  return collapsed.replace(/(^|[\s\-'’])(\p{L})/gu, (_match, before: string, letter: string) => before + letter.toLocaleUpperCase());
}

/** Trimmed and lower-cased; the shape is the validator's question, not this one's. */
export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

/** E.164 where the number places itself; one that does not is kept as typed and trimmed, so a lead's captured phone is never lost. */
export function normalizePhoneInput(value: string, countryCode = 'US'): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  try {
    return toE164(trimmed, countryCode);
  } catch (error) {
    if (error instanceof PhoneNumberError) return trimmed;
    throw error;
  }
}

/** A US code in its five- or nine-digit shape; anywhere else, and a US code the validator only warns on, as typed and trimmed. */
export function normalizePostalCode(value: string, countryCode = 'US'): string {
  const outcome = validatePostalCode(value, countryCode);
  return outcome.ok ? outcome.normalized : value.trim();
}

/**
 * The digits the phone mask shows for a stored number: a North American
 * number's ten, or what has been typed so far. Null for a number outside the
 * mask, one stored with another country's code, which the form keeps as text.
 */
export function phoneMaskDigits(stored: string): string | null {
  const trimmed = stored.trim();
  if (trimmed.startsWith('+') && !trimmed.startsWith('+1')) return null;
  const digits = trimmed.replace(/\D/gu, '');
  if (trimmed.startsWith('+1')) return digits.slice(1);
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits.slice(0, 10);
}
